import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { Heart, Menu, Moon, Search, ShoppingBag, Sun } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Logo } from "./Logo";
import { SearchDialog } from "./SearchDialog";
import { useStore } from "../context/StoreContext";

const NAV = [
  ["/product/pustak-rack", "Pustak Rack"],
  ["/shop", "Shop"],
  ["/#how-it-builds", "How it builds"],
  ["/#faq", "FAQ"],
  ["/story", "Our story"],
];

const IconBtn = ({ children, ...props }) => (
  <button className="relative grid h-10 w-10 place-items-center rounded-full transition-colors hover:bg-foreground/5" {...props}>
    {children}
  </button>
);

const Badge = ({ n, testid }) =>
  n > 0 ? (
    <span data-testid={testid} className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-sheesham px-1 text-[10px] font-semibold text-linen">{n}</span>
  ) : null;

export const Header = () => {
  const { theme, setTheme } = useTheme();
  const { count, wishlist, setCartOpen } = useStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40">
      <div data-testid="announcement-bar" className="bg-walnut text-linen">
        <div className="container-hw flex h-9 items-center justify-center gap-6 overflow-hidden whitespace-nowrap text-xs font-medium">
          <span className="hidden sm:inline">Free delivery across Hyderabad in 1 to 2 days</span>
          <span className="sm:hidden">Free delivery in Hyderabad</span>
          <span className="hidden text-brass md:inline">/</span>
          <span className="hidden md:inline">No Carpenter Needed</span>
          <span className="text-brass">/</span>
          <span>Code HW10 for 10% Off</span>
        </div>
      </div>
      <div className="border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="container-hw flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="grid h-10 w-10 place-items-center lg:hidden" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-background">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <Logo onClick={() => setMenuOpen(false)} />
                <nav className="mt-10 flex flex-col gap-5">
                  {NAV.map(([to, label]) => (
                    <Link key={to} to={to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${label.toLowerCase().replace(/ /g, "-")}`} className="font-display text-2xl">{label}</Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
            <Logo />
          </div>
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map(([to, label]) => (
              <NavLink
                key={to} to={to} end
                data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) => `text-sm font-medium transition-colors hover:text-sheesham dark:hover:text-brass ${isActive && !to.includes("#") ? "text-sheesham dark:text-brass" : ""}`}
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-0.5">
            <IconBtn data-testid="search-open-button" aria-label="Search" onClick={() => setSearchOpen(true)}><Search className="h-5 w-5" /></IconBtn>
            <IconBtn data-testid="theme-toggle-button" aria-label="Toggle dark mode" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </IconBtn>
            <Link to="/wishlist" data-testid="wishlist-link" aria-label="Wishlist" className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-foreground/5">
              <Heart className="h-5 w-5" />
              <Badge n={wishlist.length} testid="wishlist-count" />
            </Link>
            <IconBtn data-testid="cart-drawer-trigger" aria-label="Open cart" onClick={() => setCartOpen(true)}>
              <ShoppingBag className="h-5 w-5" />
              <Badge n={count} testid="cart-count" />
            </IconBtn>
          </div>
        </div>
      </div>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};
